import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  acknowledgeEmailAlert,
  exportDeliverabilityCsv,
  exportWebhookEventsCsv,
  listWebhookDeadLetters,
  retryWebhookDeadLetter,
  getAlertThresholds,
  getDeliverabilityReport,
  listEmailAlerts,
  saveAlertThresholds,
} from "@/lib/email-admin.functions";

export const Route = createFileRoute("/_authenticated/admin/email-analytics")({
  head: () => ({
    meta: [
      { title: "Deliverability — Quibblies staff" },
      {
        name: "description",
        content:
          "Send volume, failures, bounce reasons and complaint rates per notice type for notify.quibblies.com.",
      },
      { property: "og:title", content: "Deliverability — Quibblies staff" },
      {
        property: "og:description",
        content: "Track bounces and complaints per notice type and over time.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: EmailAnalyticsPage,
});

const RANGES = [7, 30, 90];

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

function EmailAnalyticsPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(getDeliverabilityReport);
  const loadAlerts = useServerFn(listEmailAlerts);
  const loadThresholds = useServerFn(getAlertThresholds);
  const saveThresholds = useServerFn(saveAlertThresholds);
  const acknowledge = useServerFn(acknowledgeEmailAlert);
  const exportCsv = useServerFn(exportDeliverabilityCsv);
  const exportEventsCsv = useServerFn(exportWebhookEventsCsv);
  const loadDeadLetters = useServerFn(listWebhookDeadLetters);
  const replayDeadLetter = useServerFn(retryWebhookDeadLetter);
  const [showResolved, setShowResolved] = useState(false);
  const [days, setDays] = useState(30);
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());

  const { data: alerts } = useQuery({
    queryKey: ["email-alerts"],
    queryFn: () => loadAlerts(),
    retry: false,
  });
  const { data: thresholds } = useQuery({
    queryKey: ["email-alert-thresholds"],
    queryFn: () => loadThresholds(),
    retry: false,
  });

  const [form, setForm] = useState({
    bounceRate: 2,
    complaintRate: 0.5,
    unsubscribeRate: 2,
    windowHours: 24,
    minimumSends: 20,
  });
  useEffect(() => {
    if (thresholds) setForm(thresholds);
  }, [thresholds]);

  const thresholdMutation = useMutation({
    mutationFn: () => saveThresholds({ data: form }),
    onSuccess: () => {
      toast.success("Alert thresholds saved");
      void queryClient.invalidateQueries({ queryKey: ["email-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["email-alert-thresholds"] });
    },
    onError: (e: Error) => toast.error("Could not save thresholds", { description: e.message }),
  });

  const ackMutation = useMutation({
    mutationFn: (alertId: string) => acknowledge({ data: { alertId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["email-alerts"] }),
    onError: (e: Error) => toast.error("Could not update alert", { description: e.message }),
  });

  const exportMutation = useMutation({
    mutationFn: () => exportCsv({ data: { from, to } }),
    onSuccess: (result) => {
      const url = URL.createObjectURL(new Blob([result.csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    },
    onError: (e: Error) => toast.error("Export failed", { description: e.message }),
  });

  const eventsExportMutation = useMutation({
    mutationFn: () => exportEventsCsv({ data: { from, to } }),
    onSuccess: (result) => {
      const url = URL.createObjectURL(new Blob([result.csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${result.count} provider events exported`);
    },
    onError: (e: Error) => toast.error("Export failed", { description: e.message }),
  });

  const { data: deadLetters } = useQuery({
    queryKey: ["email-deadletters", showResolved],
    queryFn: () => loadDeadLetters({ data: { includeResolved: showResolved } }),
    retry: false,
  });

  const replayMutation = useMutation({
    mutationFn: (id: string) => replayDeadLetter({ data: { id } }),
    onSuccess: (result) => {
      toast.success(
        result.noticeId ? "Replayed and matched to its notice" : "Replayed — no matching notice",
      );
      void queryClient.invalidateQueries({ queryKey: ["email-deadletters"] });
      void queryClient.invalidateQueries({ queryKey: ["deliverability"] });
    },
    onError: (e: Error) => toast.error("Replay failed", { description: e.message }),
  });

  const openAlerts = (alerts ?? []).filter((alert) => !alert.acknowledgedAt);

  const { data, isLoading, error } = useQuery({
    queryKey: ["deliverability", days],
    queryFn: () => load({ data: { days } }),
    retry: false,
  });

  const peak = Math.max(1, ...(data?.overTime ?? []).map((d) => d.sent + d.failed + d.bounced));

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Deliverability"
        description="How notices from notify.quibblies.com actually land: sends, failures, bounces and complaints, per notice type and over time."
      />

      {openAlerts.length > 0 ? (
        <section className="mt-6 space-y-2" aria-label="Deliverability alerts">
          {openAlerts.map((alert) => (
            <div
              key={alert.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm"
            >
              <span>
                <Badge variant="destructive" className="mr-2">
                  {alert.metric} spike
                </Badge>
                {alert.detail ??
                  `${alert.template ?? "All notices"}: ${alert.rate}% over ${alert.windowHours}h (threshold ${alert.threshold}%).`}
              </span>
              <Button
                size="sm"
                variant="soft"
                onClick={() => ackMutation.mutate(alert.id)}
                disabled={ackMutation.isPending}
              >
                Acknowledge
              </Button>
            </div>
          ))}
        </section>
      ) : (
        <p className="mt-6 rounded-xl border border-border/60 bg-surface/40 p-4 text-sm text-muted-foreground">
          No bounce, complaint or unsubscribe spikes above your thresholds right now.
        </p>
      )}

      <section className="mt-6 rounded-2xl border border-border/60 bg-surface/40 p-5">
        <h2 className="font-display text-xl text-foreground">Alert thresholds</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Measured per notice type over a rolling window. Small batches are ignored until the
          minimum number of sends is reached.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {(
            [
              ["bounceRate", "Bounce rate %"],
              ["complaintRate", "Complaint rate %"],
              ["unsubscribeRate", "Unsubscribe rate %"],
              ["windowHours", "Window (hours)"],
              ["minimumSends", "Minimum sends"],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`threshold-${key}`}>{label}</Label>
              <Input
                id={`threshold-${key}`}
                type="number"
                step={key.endsWith("Rate") ? "0.1" : "1"}
                min="0"
                value={form[key]}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, [key]: Number(event.target.value) }))
                }
              />
            </div>
          ))}
        </div>
        <Button
          className="mt-4"
          size="sm"
          onClick={() => thresholdMutation.mutate()}
          disabled={thresholdMutation.isPending}
        >
          Save thresholds
        </Button>
      </section>

      <section className="mt-6 rounded-2xl border border-border/60 bg-surface/40 p-5">
        <h2 className="font-display text-xl text-foreground">Export CSV</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Summary by notice type, or the raw provider event log (bounces, complaints,
          unsubscribes), for the selected date range.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="export-from">From</Label>
            <Input
              id="export-from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="export-to">To</Label>
            <Input
              id="export-to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <Button
            variant="soft"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? "Preparing…" : "Summary CSV"}
          </Button>
          <Button
            variant="soft"
            onClick={() => eventsExportMutation.mutate()}
            disabled={eventsExportMutation.isPending}
          >
            {eventsExportMutation.isPending ? "Preparing…" : "Event log CSV"}
          </Button>
        </div>

      </section>

      <section className="mt-6 rounded-2xl border border-border/60 bg-surface/40 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-xl text-foreground">Stuck provider events</h2>
          <Button size="sm" variant="ghost" onClick={() => setShowResolved((value) => !value)}>
            {showResolved ? "Hide resolved" : "Show resolved"}
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Events the provider sent that could not be recorded. If an export looks short, replay
          them here — the event is put through the normal path again and matched to its notice.
          {deadLetters?.unlinkedEvents
            ? ` ${deadLetters.unlinkedEvents} recorded event${deadLetters.unlinkedEvents === 1 ? "" : "s"} could not be matched to a notice and appear as “(unlinked)” in the event log CSV.`
            : ""}
        </p>

        {(deadLetters?.rows ?? []).length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Nothing stuck — every event the provider sent was recorded.
          </p>
        ) : (
          <ul className="mt-4 space-y-2">
            {(deadLetters?.rows ?? []).map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border/50 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <p className="text-foreground">
                    <Badge variant={row.resolvedAt ? "outline" : "destructive"} className="mr-2">
                      {row.eventType}
                    </Badge>
                    {row.recipient ?? "unknown recipient"}
                  </p>
                  <p className="mt-1 break-words text-xs text-muted-foreground">{row.error}</p>
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                    {row.providerEventId} · {row.attempts} attempt
                    {row.attempts === 1 ? "" : "s"} · last{" "}
                    {new Date(row.lastAttemptAt).toLocaleString()}
                  </p>
                </div>
                {row.resolvedAt ? (
                  <Badge variant="outline">resolved</Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="soft"
                    onClick={() => replayMutation.mutate(row.id)}
                    disabled={replayMutation.isPending}
                  >
                    Retry ingestion
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>



      <div className="mt-6 flex gap-2">
        {RANGES.map((range) => (
          <Button
            key={range}
            size="sm"
            variant={range === days ? "default" : "soft"}
            onClick={() => setDays(range)}
          >
            Last {range} days
          </Button>
        ))}
      </div>

      {error ? (
        <p className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
          {error.message}
        </p>
      ) : isLoading ? (
        <Skeleton className="mt-8 h-96 w-full rounded-2xl" />
      ) : (
        <>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ["Notices", data?.totals.total ?? 0],
              ["Sent", data?.totals.sent ?? 0],
              ["Failed", data?.totals.failed ?? 0],
              ["Bounced", data?.totals.bounced ?? 0],
              ["Complaints", data?.totals.complained ?? 0],
            ].map(([label, value]) => (
              <div
                key={String(label)}
                className="rounded-2xl border border-border/60 bg-surface/50 p-4"
              >
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="mt-1 font-display text-2xl text-foreground">{value}</p>
              </div>
            ))}
          </div>

          <section className="mt-10">
            <h2 className="font-display text-xl text-foreground">Volume over time</h2>
            <div className="mt-4 flex items-end gap-1 overflow-x-auto rounded-2xl border border-border/60 bg-surface/40 p-4">
              {(data?.overTime ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No notices recorded in this window.
                </p>
              ) : (
                (data?.overTime ?? []).map((day) => {
                  const total = day.sent + day.failed + day.bounced;
                  return (
                    <div
                      key={day.day}
                      className="flex w-6 shrink-0 flex-col items-center gap-1"
                      title={`${day.day}: ${day.sent} sent, ${day.failed} failed, ${day.bounced} bounced`}
                    >
                      <div
                        className="w-full rounded-t bg-primary/70"
                        style={{ height: `${Math.round((total / peak) * 120)}px` }}
                      />
                      <span className="text-[10px] text-muted-foreground">
                        {day.day.slice(8)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          <section className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="overflow-x-auto rounded-2xl border border-border/60">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">Deliverability by notice type</caption>
                <thead className="bg-surface/60 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Notice type</th>
                    <th className="px-4 py-3">Sent</th>
                    <th className="px-4 py-3">Failed</th>
                    <th className="px-4 py-3">Bounce / complaint</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.byTemplate ?? []).map((row) => (
                    <tr key={row.template} className="border-t border-border/50">
                      <td className="px-4 py-3 text-foreground">{row.template}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.sent} / {row.total}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{row.failed}</td>
                      <td className="px-4 py-3">
                        <Badge variant={row.bounceRate > 2 ? "destructive" : "outline"}>
                          {row.bounceRate}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl border border-border/60 bg-surface/40 p-5">
              <h2 className="font-display text-xl text-foreground">Bounce &amp; complaint reasons</h2>
              {(data?.bounceReasons ?? []).length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No bounces or complaints reported in this window.
                </p>
              ) : (
                <ul className="mt-3 space-y-2 text-sm">
                  {(data?.bounceReasons ?? []).map((row) => (
                    <li key={row.reason} className="flex justify-between gap-4">
                      <span className="text-muted-foreground">{row.reason}</span>
                      <span className="text-foreground">{row.count}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-4 text-xs text-muted-foreground">
                Open and click tracking is not available on Quibblies' email platform — these
                figures cover sends, failures, bounces, complaints and unsubscribes.
              </p>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-display text-xl text-foreground">Recent provider events</h2>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-border/60">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="bg-surface/60 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Event</th>
                    <th className="px-4 py-3">Recipient</th>
                    <th className="px-4 py-3">Reason</th>
                    <th className="px-4 py-3">Event ID</th>
                    <th className="px-4 py-3">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.recentEvents ?? []).map((event) => (
                    <tr key={event.id} className="border-t border-border/50">
                      <td className="px-4 py-3">
                        <Badge
                          variant={event.event_type === "unsubscribed" ? "outline" : "destructive"}
                        >
                          {event.event_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{event.recipient}</td>
                      <td className="px-4 py-3 text-muted-foreground">{event.reason ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {event.provider_event_id}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(event.received_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {(data?.recentEvents ?? []).length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-muted-foreground" colSpan={5}>
                        No provider events recorded yet. Events arrive once the site is published.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
